import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { MapPinIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { useLanguage } from '../../contexts/LanguageContext'
import { subscribeNewsletter } from '../../lib/supabaseApi'
import { supabaseConfigMissing } from '../../lib/supabaseClient'
import { goToHomePage } from '../../lib/goToHomePage'
import NewsletterSubscribeModal from '../forms/NewsletterSubscribeModal'
import FormSuccessModal from '../ui/FormSuccessModal'

const publicUrl = process.env.PUBLIC_URL || ''
const footerLogo = `${publicUrl}/img/sosvor-footer-logo-white.webp`

const QUICK_LINKS = [
  { nameKey: 'nav.home', href: '/' },
  { nameKey: 'nav.about', href: '/sobre' },
  { nameKey: 'nav.recruitment', href: '/carreira' },
  { nameKey: 'nav.news', href: '/blog' },
]

const SERVICE_ITEMS = [
  { nameKey: 'footer.svc1', href: '/servicos/engenharia-civil' },
  { nameKey: 'footer.svc2', href: '/servicos/tecnologia' },
  { nameKey: 'footer.svc7', href: '/servicos/ciberseguranca' },
  { nameKey: 'footer.svc8', href: '/servicos/gestao-processos' },
  { nameKey: 'footer.svc9', href: '/servicos/aplicacoes-gestao' },
  { nameKey: 'footer.svc3', href: '/servicos/design-marketing' },
  { nameKey: 'footer.svc4', href: '/servicos/seguranca-eletronica' },
  { nameKey: 'footer.svc5', href: '/servicos/turismo' },
  { nameKey: 'footer.svc6', href: '/servicos/imobiliario' },
]

const CONTACT_ROWS = [
  {
    icon: MapPinIcon,
    labelKey: null,
    label: 'Luanda, Angola',
    href: null,
  },
  {
    icon: PhoneIcon,
    labelKey: null,
    label: '+244 927 032 774',
    href: 'tel:+244927032774',
  },
  {
    icon: EnvelopeIcon,
    labelKey: null,
    label: 'info.sosvor@bongoslcs.com',
    href: 'mailto:info.sosvor@bongoslcs.com',
  },
  {
    icon: EnvelopeIcon,
    labelKey: null,
    label: 'geral@bongoslcs.com',
    href: 'mailto:geral@bongoslcs.com',
  },
]

const socialBtnClass =
  'flex h-10 w-10 items-center justify-center rounded-md border border-white/15 text-white/75 outline-none transition-colors transition-transform duration-200 hover:bg-white/10 hover:text-white active:scale-[0.97] active:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy'

export default function Footer() {
  const year = new Date().getFullYear()
  const { t } = useLanguage()
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false)
  const [newsletterModalOpen, setNewsletterModalOpen] = useState(false)
  const [newsletterSuccessOpen, setNewsletterSuccessOpen] = useState(false)
  const [pendingNewsletterEmail, setPendingNewsletterEmail] = useState('')
  const [newsletterSuccessTitle, setNewsletterSuccessTitle] = useState('')
  const [newsletterSuccessMessage, setNewsletterSuccessMessage] = useState('')

  const handleNewsletterSubmit = (event) => {
    event.preventDefault()
    const email = newsletterEmail.trim().toLowerCase()
    if (!email) return

    if (supabaseConfigMissing) {
      toast.error(t('footer.newsletterError'))
      return
    }

    setPendingNewsletterEmail(email)
    setNewsletterModalOpen(true)
  }

  const handleNewsletterConfirm = async ({ name, gender }) => {
    setNewsletterSubmitting(true)

    try {
      const result = await subscribeNewsletter(pendingNewsletterEmail, name, gender)
      setNewsletterModalOpen(false)
      setNewsletterEmail('')
      setPendingNewsletterEmail('')
      if (result.alreadySubscribed) {
        setNewsletterSuccessTitle(t('footer.newsletterAlreadyTitle'))
        setNewsletterSuccessMessage(t('footer.newsletterAlready'))
      } else {
        setNewsletterSuccessTitle(t('footer.newsletterSuccessTitle'))
        setNewsletterSuccessMessage(t('footer.newsletterSuccessMessage'))
      }
      setNewsletterSuccessOpen(true)
    } catch (error) {
      console.error('Newsletter subscription error:', error)
      toast.error(t('footer.newsletterError'))
    } finally {
      setNewsletterSubmitting(false)
    }
  }

  return (
    <footer className="mv-pillars-band--footer border-t border-white/10 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-16">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-12 md:gap-10 lg:gap-12">
          <div className="col-span-2 md:col-span-3">
            <a
              href="/"
              aria-label="SOSVOR — Início"
              className="inline-flex shrink-0"
              onClick={goToHomePage}
            >
              <img
                src={footerLogo}
                alt="SOSVOR"
                width={560}
                height={425}
                className="h-16 w-auto max-w-[14rem] object-contain"
                loading="lazy"
                decoding="async"
              />
            </a>
            <p className="footer-brand-tagline mt-6 max-w-md text-[13px] leading-[1.7] text-white/65">
              <span className="block md:hidden">{t('footer.taglineLine1')}</span>
              <span className="block md:hidden">{t('footer.taglineLine2')}</span>
              <span className="block md:hidden">{t('footer.taglineLine3')}</span>
              <span className="hidden md:block">{t('footer.tagline')}</span>
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://www.linkedin.com/company/sosvor1/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className={socialBtnClass}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/sosvor1"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className={socialBtnClass}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/sosvor"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className={socialBtnClass}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
            </div>
          </div>

          <div className="col-span-1 md:col-span-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-400/90">
              {t('footer.quickTitle')}
            </h3>
            <ul className="mt-5 space-y-2.5 text-[15px] font-semibold text-white/85">
              {QUICK_LINKS.map((link) => (
                <li key={link.nameKey}>
                  <Link to={link.href} className="transition-colors hover:text-white">
                    {t(link.nameKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-1 text-left md:col-span-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-400/90">
              {t('footer.servicesTitle')}
            </h3>
            <ul className="mt-5 space-y-2.5 text-[15px] font-semibold text-white/85">
              {SERVICE_ITEMS.map((service) => (
                <li key={service.nameKey}>
                  <a href={service.href} className="transition-colors hover:text-white">
                    {t(service.nameKey)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-2 md:col-span-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-400/90">
              {t('footer.contactsTitle')}
            </h3>
            <ul className="mt-5 space-y-3.5 text-[15px] font-semibold text-white/85">
              {CONTACT_ROWS.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <item.icon className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400/90" aria-hidden />
                  {item.href ? (
                    <a href={item.href} className="break-all transition-colors hover:text-white">
                      {item.labelKey ? t(item.labelKey) : item.label}
                    </a>
                  ) : (
                    <span>{item.labelKey ? t(item.labelKey) : item.label}</span>
                  )}
                </li>
              ))}
            </ul>

            <div className="footer-newsletter mt-8 border-t border-white/10 pt-6 text-left">
              <h4 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-400/90">
                {t('footer.newsletterTitle')}
              </h4>
              <p className="mt-3 text-[13px] leading-[1.6] text-white/65">
                {t('footer.newsletterLead')}
              </p>
              <form onSubmit={handleNewsletterSubmit} className="footer-newsletter-form">
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(event) => setNewsletterEmail(event.target.value)}
                  required
                  placeholder={t('footer.newsletterPlaceholder')}
                />
                <button type="submit" disabled={newsletterModalOpen || newsletterSubmitting}>
                  {t('footer.newsletterSubmit')}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center gap-4 border-t border-white/10 pt-8 md:flex-row md:items-center md:justify-between">
          <div className="order-2 w-full md:order-1 md:w-auto">
            <p className="footer-copyright mx-auto w-fit max-w-lg text-[10px] uppercase leading-relaxed tracking-[0.1em] text-white/40 sm:text-[11px] sm:tracking-[0.12em] md:mx-0">
              <span className="block">{t('footer.copyrightLine1', { year })}</span>
              <span className="block">{t('footer.copyrightLine2')}</span>
              <span className="footer-developed-by mt-2 block normal-case tracking-normal text-white/35">
                {t('footer.developedBy')}
              </span>
            </p>
          </div>
          <div className="order-1 flex w-full items-center justify-center gap-4 text-[11px] text-white/50 sm:gap-6 sm:text-[12px] md:order-2 md:w-auto md:justify-end">
            <Link to="/politica-privacidade" className="transition-colors hover:text-white">
              {t('footer.privacy')}
            </Link>
            <Link to="/termos-servico" className="transition-colors hover:text-white">
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </div>

      <NewsletterSubscribeModal
        open={newsletterModalOpen}
        email={pendingNewsletterEmail}
        submitting={newsletterSubmitting}
        onClose={() => {
          if (!newsletterSubmitting) setNewsletterModalOpen(false)
        }}
        onConfirm={handleNewsletterConfirm}
      />

      <FormSuccessModal
        open={newsletterSuccessOpen}
        onClose={() => setNewsletterSuccessOpen(false)}
        title={newsletterSuccessTitle}
        message={newsletterSuccessMessage}
      />
    </footer>
  )
}
